import { useState } from "react";
import { ArrowLeft, Minus, Plus, MapPin, Tag, Loader2, Crown, Trash2, AlertCircle, Gift } from "lucide-react";
import SidePanel from "./SidePanel";

const round2 = (n) => Math.round(n * 100) / 100;

export const formatINR = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * Cashback on a reward (rewards API): rewardtype 0 = percentage, 1 = fixed ₹; rewardvalue = the value;
 * max_reward_value = cap (0 / missing = no cap). Returns null when the reward has no cashback.
 */
export function getRewardCashback(reward) {
    const value = Number(reward?.rewardvalue) || 0;
    if (value <= 0) return null;
    return { type: Number(reward.rewardtype) === 1 ? "fixed" : "percent", value, max: Number(reward.max_reward_value) || 0 };
}

/** "5% cashback" / "₹50 cashback" */
export const cashbackTitle = (cb) => (cb.type === "percent" ? `${cb.value}% cashback` : `${formatINR(cb.value)} cashback`);

/** "5% cashback (up to ₹50)" / "₹50 cashback" */
export const cashbackLabel = (cb) =>
    cb.type === "percent" && cb.max > 0 ? `${cashbackTitle(cb)} (up to ${formatINR(cb.max)})` : cashbackTitle(cb);

/** Cashback earned on a bill of `amount`, never above the cap */
export function getCashbackAmount(cb, amount) {
    if (!cb || !(amount > 0)) return 0;
    const raw = cb.type === "percent" ? (amount * cb.value) / 100 : cb.value;
    return round2(cb.max > 0 ? Math.min(raw, cb.max) : raw);
}

/**
 * Membership needed to buy: { name, fee, owned }
 * fee is what gets added to the total (0 for a free plan or one the user already has).
 */
export const getMembershipFee = (membership) =>
    membership && !membership.owned ? Number(membership.fee) || 0 : 0;

export const getMembershipGST = (membership) => {
    const fee = getMembershipFee(membership);
    return round2(fee * 0.18);
};

export const getMembershipTotal = (membership) => {
    const fee = getMembershipFee(membership);
    return round2(fee + getMembershipGST(membership));
};

/** Value shown for a membership in a payment breakdown: "Active" (already owned), "Free" or "+₹999" */
export const membershipFeeText = (membership) =>
    membership.owned ? "Active" : getMembershipFee(membership) > 0 ? `+${formatINR(membership.fee)}` : "Free";

/** Compact "your membership" card shown at the top of the pay-bill and voucher checkout panels. */
export function MembershipSummary({ membership, onChange }) {
    if (!membership) return null;
    const fee = getMembershipFee(membership);
    return (
        <div className="flex items-center gap-3 rounded-2xl border border-[#0B67FC]/20 bg-[#0B67FC]/5 p-3.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0B67FC] text-white shadow-sm">
                <Crown size={18} />
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#0954D0]">Membership</p>
                <p className="truncate text-sm font-bold text-gray-900">{membership.name}</p>
            </div>
            <div className="shrink-0 text-right">
                <p className={`text-sm font-bold ${fee > 0 ? "text-gray-900" : "text-emerald-600"}`}>
                    {membership.owned ? "Active" : fee > 0 ? formatINR(fee) : "Free"}
                </p>
                {fee > 0 && <p className="text-[10px] text-gray-500">for 12 months</p>}
                {onChange && (
                    <button type="button" onClick={onChange} className="mt-0.5 text-[11px] font-semibold text-[#0954D0] hover:underline">
                        Change
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * Single place that maps a voucher row -> what it's worth and what the customer pays.
 * - face value        = voucher_price
 * - discount_type     = 2 -> percentage of face value, 1 -> fixed ₹ amount
 * - max_discount_value = cap on the discount that can be availed (0 / missing = no cap)
 * payable = face - discount
 */
export function getVoucherPricing(voucher) {
    const face = Number(voucher.voucher_price) || 0;
    const value = Number(voucher.discount_value) || 0;
    const cap = Number(voucher.max_discount_value) || 0;
    // customer_share is always a percentage: the customer only gets that share of the discount
    // and of the max discount cap (missing = full share)
    const share = voucher.customer_share == null || voucher.customer_share === "" ? 100 : Number(voucher.customer_share) || 0;
    const isPercent = Number(voucher.discount_type) === 2;

    const own = isPercent ? (face * value * share) / 10000 : (value * share) / 100;
    const customerCap = round2((cap * share) / 100); // 0 = no cap
    const rawDiscount = customerCap > 0 ? Math.min(own, customerCap) : own;

    const discount = round2(Math.min(Math.max(rawDiscount, 0), face));
    return {
        face,
        discount,
        payable: round2(face - discount),
        isPercent,
        pct: isPercent ? round2((value * share) / 100) : 0, // % off the customer actually gets
        cap: customerCap, // max ₹ discount the customer can get (0 = no cap)
    };
}

/** "10% OFF" / "₹50 OFF" - always the customer's share, never the raw voucher value */
export const voucherDiscountText = (voucher) => {
    const p = getVoucherPricing(voucher);
    return p.isPercent ? `${p.pct}% OFF` : `${formatINR(p.discount)} OFF`;
};

/** Stock: sold_count === no_of_voucher means sold out. no_of_voucher missing / 0 = unlimited. */
export function getVoucherAvailability(voucher) {
    const total = Number(voucher.no_of_voucher) || 0;
    const sold = Number(voucher.sold_count) || 0;
    if (total <= 0) return { soldOut: false, remaining: Infinity };
    const remaining = Math.max(total - sold, 0);
    return { soldOut: remaining === 0, remaining };
}

/** GoerCoin needed to buy ONE of this voucher (0 when gtcoin_required is not set) */
export const getVoucherCoins = (voucher) =>
    Number(voucher.gtcoin_required) === 1 ? Number(voucher.required_value) || 0 : 0;

/** redemption_type: 1 = online, 2 = offline, 3 = both */
export const redemptionLabel = (voucher) =>
    ({ 1: "Redeem online only", 2: "Redeem in store only", 3: "Redeem online & in store" })[Number(voucher.redemption_type)] || null;

/**
 * Voucher is tied to memberships (voucher.memberships = [program_id, ...]).
 * Empty / missing list = open to everyone. Otherwise the user's membership must be one of them.
 */
export const isVoucherEligible = (voucher, programId) => {
    const allowed = Array.isArray(voucher.memberships) ? voucher.memberships.map(Number) : [];
    if (allowed.length === 0) return true;
    return programId != null && allowed.includes(Number(programId));
};

export function QuantityStepper({ qty, onDec, onInc, max = 10, min = 0 }) {
    return (
        <div className="inline-flex items-center rounded-lg border border-[#0B67FC]/30 bg-white overflow-hidden">
            <button
                type="button"
                onClick={onDec}
                disabled={qty <= min}
                className="px-2.5 py-1.5 text-[#0954D0] hover:bg-[#0B67FC]/5 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
                aria-label="Remove one"
            >
                <Minus size={14} />
            </button>
            <span className="min-w-8 text-center text-sm font-semibold text-gray-900 tabular-nums">{qty}</span>
            <button
                type="button"
                onClick={onInc}
                disabled={qty >= max}
                className="px-2.5 py-1.5 text-[#0954D0] hover:bg-[#0B67FC]/5 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
                aria-label="Add one more"
            >
                <Plus size={14} />
            </button>
        </div>
    );
}

/**
 * Order Summary drawer shown after "Proceed" on the vouchers list.
 * items: [{ voucher, qty }]
 * onChangeQty(voucherId, delta) - parent owns the cart (the - button stops at 1; use onRemove to take a voucher out)
 * onRemove(voucherId) - removes the voucher from the cart (trash button)
 * membership: { name, fee, owned } - a membership is required to buy; its fee is added to the total
 * onChangeMembership: optional, shows a "Change" link on the membership card
 * onPay({ items: [{ voucher_id, quantity }], total, membershipFee, gift }) - gift is null (for self) or { name, mobile }; may return a promise
 */
export default function VoucherCheckoutPanel({
    open,
    onClose,
    store,
    items,
    onChangeQty,
    onRemove,
    onPay,
    maxQty = 10,
    membership = null,
    onChangeMembership,
    issues = {},
    canCheckout = true,
    error = ""
}) {
    const [submitting, setSubmitting] = useState(false);

    // Gift: buying the voucher(s) for someone else -> need their name + mobile
    const [isGift, setIsGift] = useState(false);
    const [giftName, setGiftName] = useState("");
    const [giftMobile, setGiftMobile] = useState("");
    const [giftTouched, setGiftTouched] = useState(false);
    const giftErrors = {
        name: giftName.trim().length < 2 ? "Enter the recipient's name" : "",
        mobile: /^[6-9]\d{9}$/.test(giftMobile) ? "" : "Enter a valid 10-digit mobile number",
    };
    const giftValid = !giftErrors.name && !giftErrors.mobile;

    const totals = items.reduce(
        (acc, { voucher, qty }) => {
            const p = getVoucherPricing(voucher);
            acc.count += qty;
            acc.face += p.face * qty;
            acc.payable += p.payable * qty;
            return acc;
        },
        { count: 0, face: 0, payable: 0 }
    );
    const face = round2(totals.face);
    const payable = round2(totals.payable);
    const savings = round2(face - payable);
    const membershipFee = getMembershipFee(membership);
    const membershipGST = getMembershipGST(membership);
    const membershipTotal = getMembershipTotal(membership);

    const total = round2(payable + membershipTotal);
    const coins = items.reduce((s, { voucher, qty }) => s + getVoucherCoins(voucher) * qty, 0);

    const address = [store?.address, store?.cityName].filter(Boolean).join(", ");

    const handlePay = async () => {
        if (submitting || items.length === 0) return;
        if (isGift && !giftValid) {
            setGiftTouched(true);
            return;
        }
        setSubmitting(true);
        try {
            await onPay?.({
                items: items.map(({ voucher, qty }) => ({ voucher_id: voucher.id, quantity: qty })),
                total,
                membershipFee,
                membershipGST,
                membershipTotal,
                // null = bought for self
                gift: isGift ? {isGift:true, name: giftName.trim(), mobile: giftMobile } : {isGift:false},
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <SidePanel open={open} onClose={onClose} label="Order summary">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-white">
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 -ml-1.5 rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                    aria-label="Back"
                >
                    <ArrowLeft size={20} />
                </button>
                <h2 className="text-lg font-bold text-gray-900">Order Summary</h2>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-gray-50/60">
            {error && (
                <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    {error}
                </div>
            )}
                {membership && !membership.owned && (
                    <MembershipSummary membership={membership} onChange={onChangeMembership} />
                )}

                {savings > 0 && (
                    <div className="flex items-center gap-2 rounded-xl border border-[#0B67FC]/30 bg-[#0B67FC]/5 px-4 py-3 text-sm font-bold text-[#083F94]">
                        <Tag size={16} className="shrink-0" />
                        Saving {formatINR(savings)} on this purchase!
                    </div>
                )}

                {items.map(({ voucher, qty }) => {
                    const p = getVoucherPricing(voucher);
                    return (
                        <div key={voucher.id} className="rounded-2xl border border-gray-100 bg-white shadow-2xs">
                            <div className="flex items-start justify-between gap-3 p-4">
                                <p className="text-sm font-semibold text-gray-900 leading-snug">{voucher.voucher_name}</p>
                                <div className="shrink-0 text-right text-sm">
                                    {p.discount > 0 && (
                                        <span className="mr-2 text-gray-400 line-through">{formatINR(p.face)}</span>
                                    )}
                                    <span className="font-bold text-gray-900">{formatINR(p.payable)}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
                                {voucher._external ? (
                                    <span className="truncate text-xs text-gray-500">Already in your cart</span>
                                ) : (
                                    <span className="flex min-w-0 items-center gap-1.5 text-sm text-gray-600">
                                        <MapPin size={14} className="shrink-0 text-[#0B67FC]" />
                                        <span className="truncate">{address || store?.store_name}</span>
                                    </span>
                                )}
                                <div className="flex shrink-0 items-center gap-2">
                                    <QuantityStepper
                                        qty={qty}
                                        min={1}
                                        max={Math.min(maxQty, getVoucherAvailability(voucher).remaining)}
                                        onDec={() => onChangeQty(voucher.id, -1)}
                                        onInc={() => onChangeQty(voucher.id, 1)}
                                    />
                                    {onRemove && (
                                        <button
                                            type="button"
                                            onClick={() => onRemove(voucher.id)}
                                            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                            aria-label="Remove voucher"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                    {issues[voucher.id] && (
                                        <p className="border-t border-rose-100 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-600 rounded-b-2xl">
                                            {issues[voucher.id]}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-2xs">
                    <label className="flex cursor-pointer items-center gap-3">
                        <input
                            type="checkbox"
                            checked={isGift}
                            onChange={(e) => { setIsGift(e.target.checked); setGiftTouched(false); }}
                            className="h-4 w-4 rounded border-gray-300 accent-[#0B67FC]"
                        />
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                            <Gift size={16} className="text-[#0B67FC]" />
                            This is a gift for someone else
                        </span>
                    </label>
                    {isGift && (
                        <div className="mt-3 space-y-3">
                            <div>
                                <input
                                    type="text"
                                    value={giftName}
                                    onChange={(e) => setGiftName(e.target.value)}
                                    placeholder="Recipient's name"
                                    maxLength={60}
                                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#0B67FC]"
                                />
                                {giftTouched && giftErrors.name && (
                                    <p className="mt-1 text-xs font-semibold text-rose-600">{giftErrors.name}</p>
                                )}
                            </div>
                            <div>
                                <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-[#0B67FC]">
                                    <span className="pl-3 text-sm text-gray-500">+91</span>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        value={giftMobile}
                                        onChange={(e) => setGiftMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                        placeholder="Recipient's mobile number"
                                        className="w-full bg-transparent px-2 py-2.5 text-sm outline-none"
                                    />
                                </div>
                                {giftTouched && giftErrors.mobile && (
                                    <p className="mt-1 text-xs font-semibold text-rose-600">{giftErrors.mobile}</p>
                                )}
                            </div>
                            <p className="text-[11px] text-gray-500">The voucher(s) will be delivered to this person.</p>
                        </div>
                    )}
                </div>

                <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-2xs">
                    <h3 className="mb-3 text-base font-bold text-gray-900">Payment Details</h3>
                    <div className="space-y-2.5 text-sm">
                        <div className="flex items-center justify-between text-gray-700">
                            <span>Voucher amount ({totals.count})</span>
                            <span className="font-semibold text-gray-900">{formatINR(face)}</span>
                        </div>
                        {savings > 0 && (
                            <div className="flex items-center justify-between text-emerald-700">
                                <span>Voucher discount</span>
                                <span className="font-semibold">-{formatINR(savings)}</span>
                            </div>
                        )}
                        {coins > 0 && (
                            <div className="flex items-center justify-between text-gray-700">
                                <span>
                                    GoerCoin required
                                    <span className="block text-[11px] text-gray-400">Not deducted from the amount</span>
                                </span>
                                <span className="font-semibold text-gray-900">{coins}</span>
                            </div>
                        )}
                        {membership && !membership.owned && (
                            <>
                                <div className="flex items-center justify-between text-gray-700">
                                    <span>Membership Fee</span>
                                    <span className={`font-semibold ${membershipFee > 0 ? "text-gray-900" : "text-emerald-700"}`}>
                                        {membershipFee > 0 ? formatINR(membershipFee) : membershipFeeText(membership)}
                                    </span>
                                </div>

                                {membershipFee > 0 && (
                                    <div className="flex items-center justify-between text-gray-700">
                                        <span>GST on Membership (18%)</span>
                                        <span className="font-semibold text-gray-900">
                                            {formatINR(membershipGST)}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
                        <span className="text-sm text-gray-700">To pay</span>
                        <span className="text-2xl font-bold text-gray-900">{formatINR(total)}</span>
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-100 bg-white px-5 py-4">
                <button
                    type="button"
                    onClick={handlePay}
                    disabled={submitting || items.length === 0 || !canCheckout}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B67FC] py-3.5 text-base font-bold text-white shadow-sm transition-all hover:bg-[#0954D0] active:scale-[0.99] disabled:opacity-60"
                >
                    {submitting && <Loader2 size={18} className="animate-spin" />}
                    Pay {formatINR(total)}
                </button>
            </div>
        </SidePanel>
    );
}